import { useRef } from 'react'
import { FolderTree, Link2 } from 'lucide-react'

export type AssociationSectionTab = 'detail' | 'summary'

export const DEFAULT_ASSOCIATION_SECTION_TAB: AssociationSectionTab = 'summary'

const TAB_ORDER: AssociationSectionTab[] = ['summary', 'detail']

const TAB_META = {
  detail: {
    label: '레슨 연결 상세',
    description: '연결 상태 · Q/S/QQS · 그룹 프리뷰',
    icon: Link2,
  },
  summary: {
    label: '커리큘럼 모듈 현황',
    description: '모듈별 연결 레슨과 출처 분포',
    icon: FolderTree,
  },
} satisfies Record<AssociationSectionTab, {
  label: string
  description: string
  icon: typeof Link2
}>

export function getAssociationTabFromKey(
  currentTab: AssociationSectionTab,
  key: string,
): AssociationSectionTab | null {
  const currentIndex = TAB_ORDER.indexOf(currentTab)
  if (key === 'Home') return TAB_ORDER[0]
  if (key === 'End') return TAB_ORDER[TAB_ORDER.length - 1]
  if (key === 'ArrowRight') return TAB_ORDER[(currentIndex + 1) % TAB_ORDER.length]
  if (key === 'ArrowLeft') return TAB_ORDER[(currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length]
  return null
}

interface Props {
  activeTab: AssociationSectionTab
  associationCount: number | null
  onChange: (tab: AssociationSectionTab) => void
}

export function AssociationSectionTabs({ activeTab, associationCount, onChange }: Props) {
  const tabRefs = useRef<Record<AssociationSectionTab, HTMLButtonElement | null>>({
    detail: null,
    summary: null,
  })

  const selectTab = (tab: AssociationSectionTab, moveFocus = false) => {
    onChange(tab)
    if (moveFocus) {
      requestAnimationFrame(() => tabRefs.current[tab]?.focus())
    }
  }

  return (
    <div
      role="tablist"
      aria-label="레슨 연결 검수 화면"
      className="grid gap-1 rounded-xl border border-slate-200 bg-slate-100/80 p-1.5 sm:grid-cols-2"
    >
      {TAB_ORDER.map((tab) => {
        const meta = TAB_META[tab]
        const Icon = meta.icon
        const selected = activeTab === tab
        return (
          <button
            key={tab}
            ref={(element) => { tabRefs.current[tab] = element }}
            id={`lesson-association-${tab}-tab`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`lesson-association-${tab}-panel`}
            tabIndex={selected ? 0 : -1}
            onClick={() => selectTab(tab)}
            onKeyDown={(event) => {
              const nextTab = getAssociationTabFromKey(tab, event.key)
              if (!nextTab) return
              event.preventDefault()
              selectTab(nextTab, true)
            }}
            className={`group flex min-h-16 items-center gap-3 rounded-lg border px-4 py-3 text-left transition ${
              selected
                ? 'border-slate-300 bg-white text-slate-900 shadow-sm'
                : 'border-transparent text-slate-500 hover:border-slate-200 hover:bg-white/70 hover:text-slate-800'
            }`}
          >
            <span className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${
              selected ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 ring-1 ring-inset ring-slate-200'
            }`}>
              <Icon className="size-4" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-sm font-bold">
                {meta.label}
                {tab === 'detail' ? (
                  <span
                    aria-label={associationCount == null ? '연결 수 조회 전' : `연결 ${associationCount}개`}
                    className="inline-flex min-w-7 justify-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-slate-600 ring-1 ring-inset ring-slate-200"
                  >
                    {associationCount == null ? '—' : associationCount}
                  </span>
                ) : null}
              </span>
              <span className="mt-0.5 block truncate text-xs font-medium text-slate-400">
                {meta.description}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}
